/**
 * Leitura do catálogo e aplicação de uma nova versão.
 *
 * O ponto crítico deste arquivo é `applyCatalog`: ele substitui todo o catálogo numa
 * única transação e **não toca em `localSignState`** (F6-T12). Favoritos, progresso,
 * histórico e arquivos já baixados sobrevivem a qualquer atualização.
 */
import { db, type CatalogMetadata } from '@/db/schema';
import type { Catalog, Category, Sign } from '@/types/catalog';

// --- leitura ---

export async function listCategories(): Promise<Category[]> {
  return db.categories.orderBy('order').toArray();
}

export async function getCategory(categoryId: string): Promise<Category | undefined> {
  return db.categories.get(categoryId);
}

export async function listSignsByCategory(categoryId: string): Promise<Sign[]> {
  return db.signs.where('[categoryId+order]').between([categoryId, -Infinity], [categoryId, Infinity]).toArray();
}

export async function getSign(signId: string): Promise<Sign | undefined> {
  return db.signs.get(signId);
}

export async function listAllSigns(): Promise<Sign[]> {
  return db.signs.toArray();
}

export async function countSigns(): Promise<number> {
  return db.signs.count();
}

export async function getCatalogMetadata(): Promise<CatalogMetadata | undefined> {
  return db.catalogMetadata.get('current');
}

export async function markCatalogChecked(at: Date = new Date()): Promise<void> {
  const current = await db.catalogMetadata.get('current');
  if (!current) return;
  await db.catalogMetadata.put({ ...current, lastCheckedAt: at.toISOString() });
}

// --- escrita ---

export interface ApplyCatalogResult {
  categories: number;
  signs: number;
  /** Sinais que sumiram da fonte e foram marcados como inativos, não apagados. */
  deactivated: string[];
  preservedStates: number;
  /** Sinais com arquivo local cujo vídeo mudou na fonte (PLAN §40). */
  mediaOutdated: string[];
}

/**
 * Aplica um catálogo validado ao banco local, em transação única (F7-T07).
 *
 * Sinais que desapareceram da fonte **não são removidos**: viram `active = false`
 * (PLAN §41). Isso mantém favorito, histórico e mídia local intactos caso o sinal
 * volte, e evita que uma quebra momentânea do catalogador destrua dados do usuário.
 */
export async function applyCatalog(
  catalog: Catalog,
  at: Date = new Date(),
): Promise<ApplyCatalogResult> {
  return db.transaction(
    'rw',
    db.categories,
    db.signs,
    db.catalogMetadata,
    db.localSignState,
    async () => {
      const incomingSignIds = new Set(catalog.signs.map((sign) => sign.id));
      const existingSignIds = await db.signs.toCollection().primaryKeys();

      // Sinais ausentes do novo catálogo: desativar, preservando o registro.
      const missing = existingSignIds.filter((id) => !incomingSignIds.has(id));
      const deactivated: Sign[] = [];
      for (const sign of await db.signs.bulkGet(missing)) {
        if (sign) deactivated.push({ ...sign, active: false });
      }

      const incomingCategoryIds = new Set(catalog.categories.map((category) => category.id));
      const staleCategories = (await db.categories.toCollection().primaryKeys()).filter(
        (id) => !incomingCategoryIds.has(id),
      );

      await db.categories.bulkDelete(staleCategories);
      await db.categories.bulkPut(catalog.categories);
      await db.signs.bulkPut([...catalog.signs, ...deactivated]);

      const metadata: CatalogMetadata = {
        id: 'current',
        schemaVersion: catalog.schemaVersion,
        catalogVersion: catalog.catalogVersion,
        contentHash: catalog.contentHash,
        generatedAt: catalog.generatedAt,
        sourceUrl: catalog.sourceUrl,
        appliedAt: at.toISOString(),
        lastCheckedAt: at.toISOString(),
      };
      await db.catalogMetadata.put(metadata);

      // --- Invalidação de mídia (F7-T11, PLAN §40) ---
      //
      // Única escrita em localSignState feita pela sincronização, e ela apenas
      // *anota* um campo: marca que o arquivo local ficou velho porque o vídeo
      // mudou na fonte. Nada é apagado — o arquivo antigo continua tocando até o
      // novo ser baixado e validado.
      //
      // A comparação usa a referência gravada no momento do download
      // (`sourceExternalId`), não o nome nem a data: só a troca real do vídeo
      // invalida o arquivo. Uma correção de acentuação no nome não invalida nada.
      const mediaOutdated: string[] = [];
      const states = await db.localSignState.toArray();

      for (const state of states) {
        if (state.offline.status !== 'available') continue;

        const incoming = catalog.signs.find((sign) => sign.id === state.signId);
        if (!incoming) continue;

        const storedRef = state.offline.sourceExternalId;
        // Sem referência gravada (arquivo importado manualmente, por exemplo), não
        // há como afirmar que ficou velho — e afirmar por engano faria o usuário
        // rebaixar sem motivo.
        if (!storedRef) continue;

        const currentRef = incoming.media.externalId;
        const outdated = currentRef !== undefined && currentRef !== storedRef;

        if (outdated !== Boolean(state.offline.outdated)) {
          await db.localSignState.put({
            ...state,
            offline: { ...state.offline, outdated },
          });
        }
        if (outdated) mediaOutdated.push(state.signId);
      }

      const preservedStates = states.length;

      return {
        categories: catalog.categories.length,
        signs: catalog.signs.length,
        deactivated: deactivated.map((sign) => sign.id),
        preservedStates,
        mediaOutdated,
      };
    },
  );
}

/**
 * Reconstrói apenas o índice derivado, sem tocar em dados pessoais nem em mídia
 * (F20-T08). Usado pela tela de diagnóstico como reparo não destrutivo.
 */
export async function clearCatalogOnly(): Promise<void> {
  await db.transaction('rw', db.categories, db.signs, db.catalogMetadata, async () => {
    await db.categories.clear();
    await db.signs.clear();
    await db.catalogMetadata.clear();
  });
}
